import get from 'lodash/get';
import { storeIds } from '../constants/store';
import { store } from '../../redux/store';
import { getLocationWithoutCountryPath } from './pathes';

/**
 * Internal Insomnia page name for Amplitude "View Page" (path without country prefix, leading slash).
 * React Router `useLocation().pathname` does not include `?query` or `#hash`. We still strip `?` / `#` so a
 * path+query string (e.g. `/menu?utm_source=google` from another caller) resolves like `/menu`.
 * @param {string} pathname
 * @returns {string|null} null when no ticket mapping (custom event not fired)
 */
export const resolveAmplitudePageName = (pathname) => {
  if (!pathname || typeof pathname !== 'string') {
    return null;
  }
  const pathOnly = pathname.split(/[?#]/)[0];
  const path = pathOnly.endsWith('/') && pathOnly.length > 1
    ? pathOnly.slice(0, -1)
    : pathOnly;

  const rules = [
    { re: /^\/$/, name: 'Home' },
    { re: /^\/home\/address$/, name: 'Home' },
    { re: /^\/menu(\/|$)/, name: 'Menu' },
    { re: /^\/shop-gift-cards(\/|$)/, name: 'Menu' },
    { re: /^\/goto\/store\//, name: 'Menu' },
    { re: /^\/shipping$/, name: 'Shipping LP' },
    { re: /^\/login$/, name: 'Sign In' },
    { re: /^\/reset-password(\/|$)/, name: 'Reset Password' },
    { re: /^\/create-account$/, name: 'Create Account (Join Rewards)' },
    { re: /^\/countries(\/|$)/, name: 'Countries' },
    { re: /^\/gifting(\/|$)/, name: 'Gifting LP' },
    { re: /^\/contact(\/|$)/, name: 'Contact' },
    { re: /^\/findaninsomnia(\/|$)/, name: 'Find a Bakery' },
    { re: /^\/insomniarewards(\/|$)/, name: 'Rewards LP' },
    { re: /^\/user\/order-history(\/|$)/, name: 'Orders' },
    { re: /^\/user\/loyalty-rewards(\/|$)/, name: 'Rewards' },
    { re: /^\/checkout\/review-order(\/|$)/, name: 'Checkout' },
    { re: /^\/checkout\/view-order(\/|$)/, name: 'Order Summary' },
    /** Box / product gift configuration (web); must be before generic `/products/` → PDP (18383). */
    { re: /^\/products\/.+\/options(\/|$)/, name: 'Gift Options' },
    { re: /^\/single-product(\/|$)/, name: 'PDP' },
    { re: /^\/products\//, name: 'PDP' },
  ];

  const match = rules.find(({ re }) => re.test(path));
  return match ? match.name : null;
};

/**
 * Page Name for nav / hamburger analytics: strip country prefix (same idea as {@link AmplitudeAnalyticsSync})
 * before {@link resolveAmplitudePageName}. Keeps "Page Name" aligned with View Page mapping.
 *
 * When `countryPathUrl` is omitted, uses `country.selectedCountry.path_url` from Redux (call sites may pass
 * explicitly when the store is not the right source, e.g. tests).
 *
 * @param {string|undefined|null} pathname - raw Router path (may include `/{country}/...`)
 * @param {string|undefined|null} [countryPathUrl] - e.g. `state.country.selectedCountry.path_url` (`uk`, `ca`). Omit to read from the store.
 * @returns {string|undefined}
 */
export const resolvePageNameForNavEvent = (pathname, countryPathUrl) => {
  let path = '';
  if (pathname != null && String(pathname).trim() !== '') {
    path = String(pathname);
  } else if (typeof window !== 'undefined' && window.location?.pathname) {
    path = String(window.location.pathname);
  } else {
    return undefined;
  }

  const pathOnly = path.split(/[?#]/)[0];

  let countryKey = '';
  if (countryPathUrl !== undefined) {
    countryKey = String(countryPathUrl ?? '').trim();
  } else {
    try {
      countryKey = get(store.getState(), 'country.selectedCountry.path_url', '') || '';
    } catch {
      countryKey = '';
    }
  }

  const withoutCountry = getLocationWithoutCountryPath(pathOnly, countryKey);
  const mapped = resolveAmplitudePageName(withoutCountry);
  return mapped || undefined;
};

/** View Page "Is Logged In" is omitted on these pages (not meaningful for auth entry). */
const AMPLITUDE_PAGE_NAMES_WITHOUT_IS_LOGGED_IN = new Set([
  'Sign In',
  'Reset Password',
  'Create Account (Join Rewards)',
]);

/**
 * Whether to send event property "Is Logged In" on View Page (true/false).
 * @param {string} pageName - value from {@link resolveAmplitudePageName}
 */
export const shouldIncludeAmplitudeIsLoggedIn = (pageName) =>
  Boolean(pageName) && !AMPLITUDE_PAGE_NAMES_WITHOUT_IS_LOGGED_IN.has(pageName);

/**
 * Event property "Address Set": user has entered a service location (store and/or address).
 * @param {{ selectedStore?: object, userAddress?: object, userToken?: string|null }} userSlice
 */
export const getAmplitudeAddressSet = (userSlice) => {
  if (!userSlice) {
    return false;
  }
  const { selectedStore, userAddress } = userSlice;
  if (selectedStore?.id) {
    if (selectedStore.id === storeIds.shipping) {
      return Boolean(
        userAddress?.address1?.trim()
        || (userAddress?.lat != null && userAddress?.lng != null),
      );
    }
    return true;
  }
  return Boolean(
    userAddress?.address1?.trim()
    || (userAddress?.lat != null && userAddress?.lng != null),
  );
};
